import React, {useCallback, useEffect, useState} from "react";
import Layout from "./layout/Layout";
import BasicText from "../../components/form-v2/basic-text";
import {BlueButton} from "../../components/ui/Buttons";
import {Client} from "../../types/userManagement";
import {ClientSelectSingle} from "../../components/form-v2/searchable-select/ClientSelect";
import {useSearchParams} from "react-router-dom";
import {apiV2} from "../../api/axiosConfig";
import {isAxiosError} from "axios";
import {useUserManagementBrowsing} from "../../lib/user-management/UserManagementBrowsingContext";
import {ToggleButton} from "./import/inputs";
import {PhoneInput} from "../../components/form-v2/phone-input/PhoneInput";
import MultiInputSingleField from "../../components/form-v2/multi-input/MultiInputSingleField";
import {RadioSelectMulti} from "../../pages/siren/components/RadioSelectMulti";
import {ImageFileSelect} from "./users/components/ImageFileSelect";
import {useUploadFile} from "../../pages/siren/hooks/useUploadFile";

const boolOptions = [
    {value: "no", label: "No"},
    {value: "yes", label: "Yes"},
]


export const ClientCustomisation: React.FC = () => {


    const ctx = useUserManagementBrowsing()

    const [client, setClient] = useState<Client>()


    return <Layout active={"customisation"}>
        <div className="flex flex-col gap-4">
            <div className="bg-white shadow-md p-4 flex flex-col gap-4">
                <h1 className="font-bold">Customise Client</h1>
                <ClientSelectSingle client={client} onChange={setClient} initialClientID={ctx.clientID || undefined}/>
            </div>
            {client && <FormMemo key={client.id} client={client}/>}
        </div>


    </Layout>
}

const Form = ({client}: { client: Client | undefined }) => {
    const [loaded, setLoaded] = useState(false)

    const [phone, setPhone] = useState("")
    const [email, setEmail] = useState("")
    const [sendAlertNGS, setSendAlertNGs] = useState<string>()
    const [insurancePhone, setInsurancePhone] = useState("")
    const [insuranceEmailLink, setInsuranceEmailLink] = useState("")
    const [importNotificationEmails, setImportNotificationEmails] = useState<string[]>([])
    const [polarisEnabled, setPolarisEnabled] = useState(false)
    const [deleteBannerImage, setDeleteBannerImage] = useState(false)
    const [modules, setModules] = useState<string[]>([])

    const [searchParams, setSearchParams] = useSearchParams();

    const [saving, setSaving] = useState<boolean>(false)
    const [saved, setSaved] = useState(false)

    const [errorMessage, setErrorMessage] = useState("")

    const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout>()
    const [savedTimeout, setSavedTimeout] = useState<NodeJS.Timeout>()
    const [receiveEmailNotifications, setReceiveEmailNotifications] = useState(false)





    const [moduleOptions, setModuleOptions] = useState<Record<string, string[]>>({})

    const [sirenBannerImageFile, setSirenBannerImageFile] = useState<File>()

    const {upload, error: uploadError} = useUploadFile(sirenBannerImageFile ?? null)






    const save = useCallback(async () => {

        if (!client) {
            return
        }
        setSaved(false)
        setSaving(true)

        try {

            const uploadID = await upload()


            await apiV2.patch(`clients/${client.id}/customisation`, {
                phone,
                email,
                sendAlertToNGS: sendAlertNGS == "yes",
                insurancePhone,
                insuranceEmailLink,
                modules,
                receiveEmailNotifications,
                importNotificationEmails,
                polarisEnabled,
                clientModules: moduleOptions,
                sirenBannerImageID: !deleteBannerImage ? uploadID : null,
                deleteSirenBannerImage: deleteBannerImage,
            })

            setSaved(true)
        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setErrorMessage(e.response.data.error)
            } else {
                setErrorMessage("an error occurred")
            }
        }
        setSaving(false)
    }, [
        client,
        phone,
        email,
        sendAlertNGS,
        insurancePhone,
        insuranceEmailLink,
        receiveEmailNotifications,
        importNotificationEmails,
        polarisEnabled,
        moduleOptions,
        modules,
        upload,
        deleteBannerImage,
    ])

    useEffect(() => {
        if (errorMessage && errorTimeout) {
            clearTimeout(errorTimeout)
        }

        const timeout = setTimeout(() => {
            setErrorMessage("")
        }, 5000)

        setErrorTimeout(timeout)
    }, [errorMessage]);

    useEffect(() => {
        if (savedTimeout) {
            clearTimeout(savedTimeout)
        }

        const timeout = setTimeout(() => {
            setSaved(false)
        }, 5000)

        setSavedTimeout(timeout)
    }, [saved]);

    useEffect(() => {
        if (!client) return
        setLoaded(false)

        searchParams.set("clientID", client.id || "")
        setSearchParams(searchParams)


        setReceiveEmailNotifications(client.receiveEmailNotifications)
        setEmail(client.alertEmailAddresses)
        setPhone(client.clientAlarmConfig.emergencyPhoneNumber)
        setInsurancePhone(client.insuranceNumber)
        setInsuranceEmailLink(client.insuranceLink)
        setImportNotificationEmails(client.importNotificationEmails)
        setPolarisEnabled(!!client.polarisEnabled)
        setModules(client.modules || [])

        const modules = client.modules || []

        setSendAlertNGs(client.sendAlertsToNGS ? "yes" : "no")

        setLoaded(true)
    }, [client]);

    const onModuleChange = useCallback((module: string, permissions: string[]) => {
        const newModuleOptions = {...moduleOptions};
        newModuleOptions[module] = permissions;
        setModuleOptions(newModuleOptions);
    }, [moduleOptions])









    if (!client) {
        return null
    }

    if (!loaded) {
        return (
            <p>Loading...</p>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">

                <div className="bg-white shadow-md p-4 flex flex-col gap-4">
                    <h2 className="font-bold">Client Settings</h2>

                    <PhoneInput onChange={setPhone} initialValue={client.clientAlarmConfig.emergencyPhoneNumber}
                                label="Emergency Phone Number"/>
                    {/* <BasicText value={phone} onChange={setPhone} label="Emergency Phone Number" />*/}
                    <BasicText value={email} onChange={setEmail} label="Emergency Email Address"/>
                    <ToggleButton text={"Receive Email Notifications"} initialValue={client.receiveEmailNotifications}
                                  onChange={setReceiveEmailNotifications}/>
                    <ToggleButton text={"Enable Polaris AI"} value={polarisEnabled} onChange={setPolarisEnabled} />

                    <label>Modules</label>
                    <RadioSelectMulti value={modules} onChange={setModules} options={[
                        {label: "Airline Risk Matrix", value:"airlineRiskMatrix"},
                        {label: "Incident Vicinity Alerts", value: "incident_vicinity_alerts"},
                        {label: "Siren", value: "siren"},
                    ]}/>



                    <BasicText value={insurancePhone} onChange={setInsurancePhone} label="Insurance Phone Number"/>
                    <BasicText value={insuranceEmailLink} onChange={setInsuranceEmailLink}
                               label="Insurance Email Link"/>

                    <MultiInputSingleField placeholder={"Enter Email Address"}
                                           label={"Import Notification Email Addresses"}
                                           initialValues={importNotificationEmails}
                                           type={"email"}
                                           onChange={setImportNotificationEmails}/>





                </div>
                <div className="bg-white shadow-md p-4 flex flex-col gap-4">
                    <h2 className="font-bold">Siren Broadcast Config</h2>
                    <label>Email Banner Image</label>
                    { !deleteBannerImage && (
                        <ImageFileSelect originalImageURL={client.sirenBannerImageURL} onChange={setSirenBannerImageFile}/>
                    )}
                    <ToggleButton text="Delete Banner Image" value={deleteBannerImage} onChange={setDeleteBannerImage}/>



                </div>
            </div>
            {errorMessage &&
                <div className="bg-red text-white text-center py-2 px-4 rounded-md">{errorMessage}</div>}
            {saved &&
                <div className="bg-green text-white text-center py-2 px-4 rounded-md">client customisation
                    saved</div>}

            <BlueButton text={"Save"} onClick={save} loading={saving}/>
        </div>
    )

}



const FormMemo = React.memo(Form, (prev, next) => {

    return prev.client?.id == next.client?.id
})
